import { useEffect, useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import PageHero from "@/components/bakery/PageHero";
import { ClipboardArt } from "@/components/bakery/Art";
import { Tag } from "@/components/common/Chips";
import { Field, inputCls } from "@/components/common/Field";
import { Loading, PrimaryBtn } from "@/components/common/ui";
import { useApi } from "@/lib/useApi";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_PERMS = ["kasir", "waste", "penyesuaian", "kartu"];
const PERM_GROUPS = [
  { label: "Transaksi", items: [
    ["dapur", "Dapur (Produksi)"], ["waste", "Tong Sampah (Waste)"], ["kasir", "Kasir (Penjualan)"],
    ["import", "Import Marketplace"], ["gudang", "Gudang Masuk (Purchase)"], ["penyesuaian", "Penyesuaian Stok"],
  ] },
  { label: "Laporan & Kartu Stok", items: [
    ["kartu", "Lihat Kartu Stok"], ["laporan", "Laporan"],
  ] },
];

const blank = { name: "", email: "", password: "", role: "staff", active: true, permissions: DEFAULT_PERMS };

function UserDialog({ open, user, onClose, onSaved }) {
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(user ? { ...blank, ...user, password: "", permissions: user.permissions || [] } : blank); }, [open, user]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));
  const togglePerm = (key) => setF((x) => ({ ...x, permissions: x.permissions.includes(key) ? x.permissions.filter((p) => p !== key) : [...x.permissions, key] }));
  const save = async () => {
    setSaving(true);
    try {
      const perms = f.role === "admin" ? [] : f.permissions;
      if (f.id) await api.put(`/users/${f.id}`, { name: f.name, role: f.role, active: f.active, password: f.password || null, permissions: perms });
      else await api.post("/users", { name: f.name, email: f.email, password: f.password, role: f.role, permissions: perms });
      toast.success("User tersimpan");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="user-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{f.id ? "Ubah User" : "Tambah User"}</DialogTitle>
          <DialogDescription>Akun dibuat oleh Admin. Atur akses halaman tiap staf di bawah.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <Field label="Nama" required><input value={f.name} onChange={set("name")} className={inputCls} data-testid="user-name" /></Field>
          <Field label="Email" required><input type="email" value={f.email} onChange={set("email")} disabled={Boolean(f.id)} className={inputCls} data-testid="user-email" /></Field>
          <Field label={f.id ? "Password baru (opsional)" : "Password"} required={!f.id} hint="Minimal 6 karakter">
            <input type="password" value={f.password} onChange={set("password")} className={inputCls} data-testid="user-password" autoComplete="new-password" />
          </Field>
          <Field label="Role"><select value={f.role} onChange={set("role")} className={inputCls} data-testid="user-role"><option value="staff">Staff</option><option value="admin">Admin</option></select></Field>
          {f.id && <label className="flex items-center gap-2 text-sm font-medium"><Switch checked={f.active} onCheckedChange={set("active")} data-testid="user-active" /> Aktif</label>}
          {f.role === "admin" ? (
            <div className="rounded-lg border-2 border-ink bg-zinc-50 px-3 py-2.5 text-sm font-medium" data-testid="user-admin-note">Admin memiliki akses penuh ke semua halaman.</div>
          ) : (
            <div className="space-y-3" data-testid="user-permissions">
              <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Akses Halaman</div>
              {PERM_GROUPS.map((g) => (
                <div key={g.label} className="rounded-lg border border-zinc-200 p-3">
                  <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-zinc-400">{g.label}</div>
                  <div className="space-y-2.5">
                    {g.items.map(([key, label]) => (
                      <label key={key} className="flex cursor-pointer items-center justify-between gap-3 text-sm">
                        <span>{label}</span>
                        <Switch checked={f.permissions.includes(key)} onCheckedChange={() => togglePerm(key)} data-testid={`perm-${key}`} />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-zinc-500">Rak Roti selalu bisa dibuka. Master, Mapping, User, dan Pengaturan khusus Admin.</p>
            </div>
          )}
          <PrimaryBtn className="w-full" onClick={save} disabled={saving || !f.name || !f.email || (!f.id && f.password.length < 6)} data-testid="user-save">{saving ? "Menyimpan…" : "Simpan User"}</PrimaryBtn>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MasterUser() {
  const { user: me } = useAuth();
  const { data, loading, reload } = useApi("/users");
  const [edit, setEdit] = useState(undefined);
  return (
    <div data-testid="master-user-page">
      <PageHero testid="user-hero" eyebrow="Master" title="User" Art={ClipboardArt} compact desc="Admin: akses penuh. Atur halaman yang bisa dibuka tiap staf lewat tombol Ubah."
        actions={<PrimaryBtn onClick={() => setEdit(null)} data-testid="add-user-btn"><Plus className="h-4 w-4" /> Tambah User</PrimaryBtn>} />
      {loading && !data ? <Loading /> : (
        <div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white">
          <table className="dense-table w-full" data-testid="user-table">
            <thead><tr><th>Nama</th><th>Email</th><th>Role</th><th>Akses</th><th>Status</th><th /></tr></thead>
            <tbody>{(data || []).map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`}>
                <td className="font-semibold">{u.name}{u.id === me?.id && <span className="ml-2 text-[11px] text-zinc-400">(Anda)</span>}</td><td>{u.email}</td>
                <td>{u.role === "admin" ? <Tag>Admin</Tag> : <Tag tone="soft">Staff</Tag>}</td>
                <td className="text-xs text-zinc-600" data-testid={`user-access-${u.email}`}>{u.role === "admin" ? "Penuh" : `${(u.permissions || []).length} halaman`}</td>
                <td>{u.active ? "Aktif" : <Tag tone="red">Non-aktif</Tag>}</td>
                <td><button onClick={() => setEdit(u)} className="press grid h-8 w-8 place-items-center rounded border border-zinc-300 hover:border-ink" data-testid={`edit-user-${u.email}`}><Pencil className="h-3.5 w-3.5" /></button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <UserDialog open={edit !== undefined} user={edit} onClose={() => setEdit(undefined)} onSaved={() => { setEdit(undefined); reload(); }} />
    </div>
  );
}
